import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ChevronLeftIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getTefl } from '../../../api/tefl/get-tefl'
import { updateTefl } from '../../../api/tefl/update-tefl'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import OlTeflSejourTable from '../../../components/pages/ols/ol-tefl/ol-tefl-sejour-table'
import TeflForm from '../../../components/pages/tefl/tefl-form'
import TeflLecComments from '../../../components/pages/tefl/tefl-lec-comments'
import TeflOlCommentTable from '../../../components/pages/tefl/tefl-ol-comments-table'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { Tefl } from '../../../lib/types/models/tefl/tefl'
import { TeflInput } from '../../../lib/types/models/tefl/tefl-input'
import { UpdateInput } from '../../../lib/types/utils'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapTeflToInput } from './utils'

const TeflUpdatePage = () => {
  const { id, olId } = useParams<{ id: string; olId: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const { t } = useTranslate()

  const hasValidParams = !!id && !isNaN(+id) && !!olId && !isNaN(+olId)

  const { data: tefl, isPending } = useQuery({
    queryKey: ['tefl', id],
    queryFn: () => getTefl(Number(id), Number(olId)),
    enabled: hasValidParams
  })

  const { mutateAsync } = useMutation<Tefl | undefined, Error, UpdateInput<TeflInput>, unknown>({
    mutationFn: (data) => updateTefl(Number(olId), data),
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['tefl', id] })
      toast.success(t('tefl.success.message'))
    }
  })

  if (!hasValidParams) return <ItemNotFound title="Identifiant de Tefl invalide" />

  if (isPending) return <LoadingScreen />
  if (!tefl) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={toFullName(tefl.firstName, tefl.lastName)}
        icon={ChevronLeftIcon}
        onIconClick={() => navigate(Paths.OL_TEFL_LIST_PAGE, { replace: true })}
      />
      <div className={'h-4'} />

      <Box>
        <TeflForm
          defaultValues={mapTeflToInput(tefl)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.OL_TEFL_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: tefl?.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: tefl?.id, ...values })
              navigate(Paths.OL_TEFL_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: tefl?.id, ...values })
              navigate(Paths.OL_TEFL_LIST_PAGE, { replace: true })
            },
            isEnglish: true
          })}
        />
      </Box>

      <div className={'h-4'} />

      <OlTeflSejourTable olId={Number(olId)} teflId={Number(id)} />

      <div className={'h-4'} />
      <Box>
        <TeflOlCommentTable id={Number(id)} />
        <div className={'h-4'} />
        <TeflLecComments id={Number(id)} />
      </Box>
    </div>
  )
}

export default TeflUpdatePage
